/**
 * Reports Services
 *
 * SECURITY FIX: All API calls now use makeApiCall for consistent JWT auth.
 * Previously, several calls used raw axios.post() without auth headers.
 */
import { API_URL } from '../config';
import { makeApiCall } from '../_common/api.utils';
export const fetchReportsData = async (data: any) => {
	try {
		let URL = '';
		if (process.env.NODE_ENV === 'development') console.log('fetchReportsData called with data:', data);

		switch (data.getMethod) { 
			case 'GetAttendanceReport':
				URL = `${API_URL}GetAttendanceReport`;
				break;
			case 'GetLeaveReport':
				URL = `${API_URL}GetLeaveReport`;
				break;
			case 'GetDesignationWiseAttendanceReport':
				URL = `${API_URL}GetDesignationWiseAttendanceReport`;
				break;
			case 'GetSectionWiseAttendanceReport':
				URL = `${API_URL}GetSectionWiseAttendanceReport`;
				break;
			case 'GetManagementLeaveReport':
				URL = `${API_URL}GetManagementLeaveReport`;
				break;
			case 'GetDesignationWiseLeaveReport':
				URL = `${API_URL}GetDesignationWiseLeaveReport`;
				break;
			case 'GetMonthlyAttendanceSummaryReport':
				URL = `${API_URL}GetMonthlyAttendanceSummaryReport`;
				break;
			default:
				if (process.env.NODE_ENV === 'development') console.error('Invalid getMethod:', data.getMethod);
				throw new Error(`Invalid getMethod: ${data.getMethod}`);
		}
		if (process.env.NODE_ENV === 'development') console.log('Making API request to:', URL);
		if (process.env.NODE_ENV === 'development') console.log('Request data:', JSON.stringify(data, null, 2));

		/**
		 * SECURITY FIX: Use makeApiCall for authenticated requests with JWT.
		 * Previously used raw axios.post() without auth headers.
		 */
		const response = await makeApiCall(URL, data);

		if (process.env.NODE_ENV === 'development') console.log('Raw API response:', response);

		if (!response.data) {
			if (process.env.NODE_ENV === 'development') console.error('No data in response');
			throw new Error('No data received from server');
		}

		// If response indicates an error, throw it here
		if (response.data.status === 'error') {
			if (process.env.NODE_ENV === 'development') console.error('Server returned error:', response.data);
			throw new Error(response.data.message_text || 'Server returned an error');
		}

		// Process the response based on report type
		switch (data.getMethod) {
			case 'GetManagementLeaveReport':
			case 'GetDesignationWiseLeaveReport':
				// These reports have a specific structure that should be preserved
				return {
					data: {
						status: 'success',
						data: response.data.data || [],
					},
				};

			case 'GetMonthlyAttendanceSummaryReport':
				// Monthly attendance summary report returns structured data
				return {
					data: {
						status: 'success',
						data: response.data.data || {},
					},
				};

			case 'GetDesignationWiseAttendanceReport':
				// This report returns multiple arrays that need to be preserved
				if (response.data && typeof response.data === 'object') {
					return {
						data: {
							status: 'success',
							data: response.data,
						},
					};
				}
				break;

			case 'GetSectionWiseAttendanceReport':
				// Section-wise reports need their structure preserved
				if (response.data && response.data.data) {
					return {
						data: {
							status: 'success',
							data: response.data.data,
						},
					};
				}
				break;

			case 'GetLeaveReport':
				// Leave reports return an array that needs to be processed
				if (response.data && response.data.data) {
					const processedData = response.data.data.map((row: any, index: number) => {
						const newRow: any = {};
						Object.entries(row).forEach(([key, value]) => {
							if (key in newRow) {
								newRow[`${key}_${index}`] = value;
							} else {
								newRow[key] = value;
							}
						});
						return newRow;
					});
					return {
						data: {
							status: 'success',
							data: processedData,
						},
					};
				}
				break;

			default:
				// For regular reports (like attendance) that return arrays
				if (process.env.NODE_ENV === 'development') console.log('Processing default case for:', data.getMethod);
				if (process.env.NODE_ENV === 'development') console.log('Response data for default case:', response.data);

				if (response.data && response.data.status === 'success' && response.data.data) {
					if (process.env.NODE_ENV === 'development') console.log('Found success response with data array');
					const processedData = response.data.data.map((row: any, index: number) => {
						const newRow: any = {};
						Object.entries(row).forEach(([key, value]) => {
							if (key in newRow) {
								newRow[`${key}_${index}`] = value;
							} else {
								newRow[key] = value;
							}
						});
						return newRow;
					});
					return {
						data: {
							status: 'success',
							data: processedData,
						},
					};
				} else if (response.data && Array.isArray(response.data)) {
					if (process.env.NODE_ENV === 'development') console.log('Found direct array response');
					const processedData = response.data.map((row: any, index: number) => {
						const newRow: any = {};
						Object.entries(row).forEach(([key, value]) => {
							if (key in newRow) {
								newRow[`${key}_${index}`] = value;
							} else {
								newRow[key] = value;
							}
						});
						return newRow;
					});
					return {
						data: {
							status: 'success',
							data: processedData,
						},
					};
				}
		}

		// If we got here and haven't returned, log what we received
		if (process.env.NODE_ENV === 'development') console.error('Unhandled response format:', response.data);
		throw new Error('Invalid response format from server');
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('API Error:', error);
		if (process.env.NODE_ENV === 'development') console.error('Error details:', {
			message: error.message,
			response: error.response?.data,
			status: error.response?.status,
			config: error.config,
		});

		// Return a structured error response
		return {
			data: {
				status: 'error',
				message_text:
					error.response?.data?.message_text ||
					error.message ||
					'An error occurred while fetching data',
			},
		};
	}
};

export const getManagementLeaveReportService = async (payload: {
	selectedMonth: number | string;
	selectedYear?: number;
	organisationID: number;
	employeeType?: number;
}) => {
	const URL = `${API_URL}GetManagementLeaveReport`;
	const data = {
		action: 'GetManagementLeaveReport',
		selectedMonth: payload.selectedMonth,
		organisationID: payload.organisationID,
		employeeType: payload.employeeType ?? 0, // Default to permanent employees
	};

	/** SECURITY FIX: Use makeApiCall for JWT auth */
	return makeApiCall(URL, data);
};

export const getDesignationLeaveReportService = async (payload: {
	selectedMonth: string;
	selectedYear?: string;
	organisationID: number | string;
}) => {
	const URL = `${API_URL}GetDesignationWiseLeaveReport`;
	const data: any = {
		selectedMonth: payload.selectedMonth,
		organisationID: payload.organisationID,
	};

	// Add selectedYear if provided (for yearly reports)
	if (payload.selectedYear) {
		data.selectedYear = payload.selectedYear;
	}

	if (process.env.NODE_ENV === 'development') console.log('Sending data to backend:', data);
	const requestData = {
		getDesignationLeaveReportServiceToken: data,
	};

	return makeApiCall(URL, requestData);
};

export const getMonthlyAttendanceSummaryReportService = async (payload: {
	selectedMonth: number;
	organisationID: number;
}) => {
	const URL = `${API_URL}GetMonthlyAttendanceSummaryReport`;
	const data = {
		action: 'GetMonthlyAttendanceSummaryReport',
		selectedMonth: payload.selectedMonth,
		organisationID: payload.organisationID,
	};

	if (process.env.NODE_ENV === 'development') console.log('API URL:', URL);
	if (process.env.NODE_ENV === 'development') console.log('Request params:', data);

	try {
		const requestData = {
			getMonthlyAttendanceSummaryReportServiceToken: data,
		};

		const response = makeApiCall(URL, requestData);

		if (process.env.NODE_ENV === 'development') console.log('Monthly Attendance Summary Report API Response:', (await response).data);

		if ((await response).data.status === 'success') {
			const data = (await response).data.data;

			// Validate the data structure
			if (!data || typeof data !== 'object') {
				throw new Error('Invalid data structure received from server');
			}

			// Return a clean, serializable object
			return {
				month: String(data.month || ''),
				totalEmployees: Number(data.totalEmployees || 0),
				workingDays: Number(data.workingDays || 0),
				totalManDays: Number(data.totalManDays || 0),
				presentCount: Number(data.presentCount || 0),
				absentCount: Number(data.absentCount || 0),
				leaveCount: Number(data.leaveCount || 0),
				presentPercentage: String(data.presentPercentage || '0'),
				absentPercentage: String(data.absentPercentage || '0'),
				leavePercentage: String(data.leavePercentage || '0'),
			};
		} else {
			throw new Error(
				(await response).data.message_text ||
					'Failed to fetch monthly attendance summary report',
			);
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getMonthlyAttendanceSummaryReportService:', error);
		throw error;
	}
};

export const getEmployeesService = async (payload: { organisationID: number | string }) => {
	const URL = `${API_URL}GetAllActiveEmployees`;
	const data = {
		getMethod: 'GetAllActiveEmployees',
		organisationID: payload.organisationID,
	};

	if (process.env.NODE_ENV === 'development') console.log('API URL:', URL);
	if (process.env.NODE_ENV === 'development') console.log('Request params:', data);

	try {
		/** SECURITY FIX: Use makeApiCall for JWT auth */
		const response = await makeApiCall(URL, data);

		if (process.env.NODE_ENV === 'development') console.log('Get All Active Employees API Response:', response.data);

		if (response.data.status === 'success') {
			return response.data.data || [];
		} else {
			throw new Error(response.data.message_text || 'Failed to fetch employees');
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getEmployeesService:', error);
		throw error;
	}
};

export const getEmployeeLeaveReportService = async (payload: {
	employeeID: string;
	selectedYear: string;
	organisationID: number | string;
}) => {
	const URL = `${API_URL}GetEmployeeLeaveReport`;
	const data = {
		employeeID: payload.employeeID,
		selectedYear: payload.selectedYear,
		organisationID: payload.organisationID,
	};

	if (process.env.NODE_ENV === 'development') console.log('API URL:', URL);
	if (process.env.NODE_ENV === 'development') console.log('Request params:', data);

	try {
		/** SECURITY FIX: Use makeApiCall for JWT auth */
		const response = await makeApiCall(URL, data);

		if (process.env.NODE_ENV === 'development') console.log('Get Employee Leave Report API Response:', response.data);

		if (response.data.status === 'success') {
			return response.data.data;
		} else {
			throw new Error(response.data.message_text || 'Failed to fetch employee leave report');
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getEmployeeLeaveReportService:', error);
		throw error;
	}
};

export const getMonthlyCheckoutReportService = async (payload: {
	selectedMonth: number;
	organisationID: number;
	employeeType: number;
}) => {
	const URL = `${API_URL}GetMonthlyCheckoutReport`;
	const data = {
		selectedMonth: payload.selectedMonth,
		organisationID: payload.organisationID,
		employeeType: payload.employeeType,
	};

	if (process.env.NODE_ENV === 'development') console.log('API URL:', URL);
	if (process.env.NODE_ENV === 'development') console.log('Request params:', data);
	if (process.env.NODE_ENV === 'development') console.log('Employee Type being sent:', payload.employeeType);

	try {
		/** SECURITY FIX: Use makeApiCall for JWT auth */
		const response = await makeApiCall(URL, data);

		if (process.env.NODE_ENV === 'development') console.log('Get Monthly Checkout Report API Response:', response.data);

		if (response.data.status === 'success') {
			return response.data.data;
		} else {
			throw new Error(
				response.data.message_text || 'Failed to fetch monthly checkout report',
			);
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getMonthlyCheckoutReportService:', error);
		throw error;
	}
};

export const getTransferHistoryService = async (payload: {
	employeeID: string;
	organisationID: number | string;
}) => {
	const URL = `${API_URL}GetTransferHistory`;
	const data = {
		employeeID: payload.employeeID,
		organisationID: payload.organisationID,
	};

	try {
		const response = await makeApiCall(URL, data, 'POST');

		if (response.data?.status === 'success') {
			return response.data.data;
		}
		throw new Error(response.data?.message_text || 'Failed to fetch transfer history');
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getTransferHistoryService:', error);
		throw error;
	}
};
